import { randomUUID } from "@/lib/randomUUID";

type AuthSyncMessage =
  | {
    type: "auth:changed";
    senderId: string;
    ts: number;
  }
  | {
    type: "auth:logout";
    senderId: string;
    ts: number;
  };

const CHANNEL_NAME = "wyni:auth";
const STORAGE_KEY = "wyni:auth:changed";

const senderId = (() => {
  try {
    return randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
})();

const broadcastChannel: BroadcastChannel | null = (() => {
  try {
    return typeof BroadcastChannel !== "undefined"
      ? new BroadcastChannel(CHANNEL_NAME)
      : null;
  } catch {
    return null;
  }
})();

export function broadcastAuthChanged() {
  broadcastAuthMessage("auth:changed");
}

export function broadcastAuthLoggedOut() {
  broadcastAuthMessage("auth:logout");
}

function broadcastAuthMessage(type: AuthSyncMessage["type"]) {
  const message: AuthSyncMessage = {
    type,
    senderId,
    ts: Date.now(),
  };

  try {
    broadcastChannel?.postMessage(message);
  } catch {
    // Ignore.
  }

  // Fallback for environments without BroadcastChannel (or where it’s blocked).
  // This also helps when the app is open in multiple tabs.
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
  } catch {
    // Ignore.
  }
}

export function subscribeAuthChanges(handler: (message: AuthSyncMessage) => void) {
  const onBroadcastMessage = (event: MessageEvent<AuthSyncMessage>) => {
    const data = event.data;
    if (!data || (data.type !== "auth:changed" && data.type !== "auth:logout")) return;
    if (data.senderId === senderId) return;
    handler(data);
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      const parsed = JSON.parse(event.newValue) as AuthSyncMessage;
      if (parsed.type !== "auth:changed" && parsed.type !== "auth:logout") return;
      if (parsed.senderId === senderId) return;
      handler(parsed);
    } catch {
      // Ignore.
    }
  };

  try {
    broadcastChannel?.addEventListener("message", onBroadcastMessage);
  } catch {
    // Ignore.
  }
  window.addEventListener("storage", onStorage);

  return () => {
    try {
      broadcastChannel?.removeEventListener("message", onBroadcastMessage);
    } catch {
      // Ignore.
    }
    window.removeEventListener("storage", onStorage);
  };
}
